import {
  ChartingLibraryFeatureset,
  ColorTypes,
  CreateShapeOptions,
  EntityId,
  IChartingLibraryWidget,
  IOrderLineAdapter,
  LanguageCode,
  ResolutionString,
  TimeFrameItem,
  Timezone,
  VisiblePriceRange,
  widget,
} from "charting_library";
import { hexToRGB } from "@shared/utils/misc";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { LineAnnotation, PointAnnotation } from "@shared/types";
import {
  filteredAnnotationsByType,
  getChartStartRange,
  getNewPriceRange,
  getResolutionByTimeFrame,
  getStartTimeframe,
  isCloseNumber,
} from "common/Charts/utils";
import moment from "moment-timezone";
import {
  SECONDS_IN_DAY,
  SECONDS_IN_MINUTE,
  TIME_RANGE_1_DAY,
  TIME_RANGE_1_HOUR,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_1_YEAR,
  TIME_RANGE_3_MONTHS,
  TIME_RANGE_START,
  TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME,
} from "@shared/common/constants";
import { isEqual } from "lodash";
import { TimeRange } from "types";
import { getApiBaseUrl } from "app/shared/utils";
import { usePrevious, useSymbolsMetadata } from "@shared/utils/hooks/misc";
import * as Sentry from "@sentry/react";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { pinChartCanvasSizing } from "common/Charts/tradingViewPixelRatio";

const useTradingViewChart = (
  propsSymbol: string | undefined,
  exchange: string | undefined,
  timeRanges: string[],
  annotations?: (LineAnnotation | PointAnnotation)[],
  isCard?: boolean,
  minimize?: boolean,
  showExtendButton?: boolean,
  defaultTimeRange?: TimeRange,
  startTimestamp?: number,
  endTimestamp?: number,
  shouldFitToOrders?: boolean,
  object?: string
) => {
  const theme = useTheme();
  const isDarkMode = theme.isDarkMode;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const userTimezone = moment.tz.guess() as Timezone;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { isSymbolActiveInExchange } = useSymbolsMetadata();
  const isValidInExchange = isSymbolActiveInExchange(propsSymbol, exchange);
  const symbol = isValidInExchange ? `${exchange}:${propsSymbol}` : undefined;
  const timeFrame = defaultTimeRange || TIME_RANGE_1_DAY;

  const [tvWidget, setTvWidget] = useState<IChartingLibraryWidget | undefined>(
    undefined
  );
  const [datafeedRetryCount, setDatafeedRetryCount] = useState(0);
  const datafeedLoadErrorReported = useRef(false);
  const tvWidgetRef = useRef<IChartingLibraryWidget>(undefined);
  const [initialPriceScale, setInitialPriceScale] = useState<
    VisiblePriceRange | null | undefined
  >(undefined);
  const [extendToAnnotationsRange, setExtendToAnnotationsRange] =
    useState<VisiblePriceRange | null>();
  const ordersRef = useRef<IOrderLineAdapter[]>([]);
  const entityIdsRef = useRef<EntityId[]>([]);
  const pointsEntityIdsRef = useRef<EntityId[]>([]);
  const onExtendClick = useRef<any>(undefined);
  const [prevLineAnnotations, setPrevLineAnnotations] = useState<
    LineAnnotation[] | undefined
  >(undefined);
  const [symbolToLoad, setSymbolToLoad] = useState<string>();
  const [symbolChanged, setSymbolChanged] = useState(false);

  const [isInitialized, setIsInitialized] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(0);
  const prevRequestInProgress = usePrevious(requestInProgress);
  const initializedPoints = useRef(false);
  const [lowestVisibleTimestamp, setLowestVisibleTimestamp] =
    useState<number>();

  const {
    currentData: minimumTimestampData,
    isFetching: isLoadingMinimumTimestamp,
  } = sharedLib.sharedApi.bots.useGetMinimumBacktestingTimestampQuery(
    exchange && propsSymbol && isValidInExchange
      ? { exchange, symbol: propsSymbol, include_site_db: true }
      : skipToken
  );
  const minimumTimestamp = minimumTimestampData?.minimum_timestamp;

  const isLoading = !tvWidget || !isInitialized || isLoadingMinimumTimestamp;

  const lineAnnotations = useMemo(
    () => filteredAnnotationsByType<LineAnnotation>(annotations, "line"),
    [annotations]
  );
  const pointAnnotations = useMemo(
    () => filteredAnnotationsByType<PointAnnotation>(annotations, "point"),
    [annotations]
  );
  const pointAnnotationsRef = useRef<PointAnnotation[]>([]);
  pointAnnotationsRef.current = pointAnnotations;

  const getSourcePriceScale = () => {
    return tvWidgetRef.current
      ?.chart()
      ?.getPanes()?.[0]
      ?.getMainSourcePriceScale();
  };

  const getVisiblePriceRange = () => {
    try {
      return getSourcePriceScale()?.getVisiblePriceRange();
    } catch (e) {
      Sentry.captureException(
        `Encountered error when running getVisiblePriceRange in TradingView chart: ${e}`
      );
    }
  };

  const isPriceRangeChanged = () => {
    const currentPriceRange = getVisiblePriceRange();
    if (currentPriceRange && extendToAnnotationsRange) {
      const percent = 0.005;
      return (
        !isCloseNumber(
          extendToAnnotationsRange.from,
          currentPriceRange.from,
          percent
        ) ||
        !isCloseNumber(
          extendToAnnotationsRange.to,
          currentPriceRange.to,
          percent
        )
      );
    }
  };

  const setAutoScale = (isAuto = true) => {
    try {
      getSourcePriceScale()?.setAutoScale(isAuto);
    } catch (e) {
      Sentry.captureException(
        `Encountered error when running setAutoScale in TradingView chart: ${e}`
      );
    }
  };

  useEffect(() => {
    if (requestInProgress) {
      const clearLoadingTimeout = setTimeout(() => {
        setRequestInProgress(0);
      }, 1000);
      return () => {
        clearTimeout(clearLoadingTimeout);
      };
    }
  }, [requestInProgress]);

  useEffect(() => {
    if (tvWidget && !requestInProgress && prevRequestInProgress) {
      setInitialPriceScale(getVisiblePriceRange());
    }
  }, [tvWidget, requestInProgress, prevRequestInProgress]);

  const [closestLineAnnotationsPrices, allLineAnnotationsPrices] =
    useMemo(() => {
      const closestOrderLines = lineAnnotations?.filter(
        (annotation) => !annotation.isSimpleLine
      );
      return [
        closestOrderLines?.map((annotation) => annotation.yMax),
        lineAnnotations?.map((annotation) => annotation.yMax),
      ];
    }, [lineAnnotations]);

  const chartColors = {
    upColor: theme.colors.green,
    downColor: theme.colors.red,
    graphColor: theme.colors.primaryBlue,
  };
  // The new look draws every chart on a panel, so it always takes the card surface.
  const chartBgColor =
    isCard || theme.isNewLook ? theme.colors.cardBgColor : theme.colors.bgColor;

  const chartGridColor = theme.isNewLook
    ? hexToRGB(theme.colors.secondaryText, 0.08)
    : theme.colors.lightestBorderGrey;

  useEffect(() => {
    if (tvWidgetRef.current) {
      const availableTimezones = tvWidgetRef.current
        .chart()
        .getTimezoneApi()
        .availableTimezones()
        .map((timezone) => timezone.id);
      const timezone = availableTimezones?.includes(userTimezone)
        ? userTimezone
        : "Etc/UTC";
      tvWidgetRef.current.chart().getTimezoneApi().setTimezone(timezone);
    }
  }, [userTimezone, tvWidget]);

  useEffect(() => {
    tvWidget?.changeTheme(isDarkMode ? "Dark" : "Light").then(() => {
      tvWidget.applyOverrides(getChartOverrides());
    });
    ordersRef.current.map((order) => {
      if (!order.getText().length)
        order.setQuantityBackgroundColor(chartBgColor);
      else order.setBodyBackgroundColor(chartBgColor);
    });
  }, [isDarkMode]);

  useEffect(() => {
    if (tvWidgetRef.current && symbol) {
      const interval =
        tvWidgetRef.current.symbolInterval().interval ||
        ("5" as ResolutionString);
      onIntervalOrSymbolChange(symbol, interval, true);
    }
  }, [symbol]);

  useEffect(() => {
    if (!tvWidgetRef.current) return;
    const shouldUpdateOrdersAndPriceRange =
      symbolToLoad === symbol?.toUpperCase() &&
      (!isInitialized ||
        symbolChanged ||
        !isEqual(prevLineAnnotations, lineAnnotations));

    if (shouldUpdateOrdersAndPriceRange) {
      updateOrderLines(lineAnnotations, symbolChanged);
      const isAuto = getSourcePriceScale()?.isAutoScale();
      const isExtendToAnnotations = !isPriceRangeChanged();
      const shouldUpdatePriceRange =
        initialPriceScale &&
        (shouldFitToOrders ||
          symbolChanged ||
          (isExtendToAnnotations && (!isAuto || !isInitialized)));

      if (shouldUpdatePriceRange) {
        setPriceRangeOnChange(tvWidgetRef.current, initialPriceScale, true);
        if (!isInitialized) setIsInitialized(true);
        if (symbolChanged) setSymbolChanged(false);
      }
    }
  }, [symbolToLoad, symbol, symbolChanged, initialPriceScale, lineAnnotations]);

  const clearPointsAnnotations = () => {
    pointsEntityIdsRef.current.map((entityId) =>
      tvWidgetRef.current?.chart().removeEntity(entityId)
    );
    pointsEntityIdsRef.current = [];
  };

  const drawPointsAnnotations = () => {
    clearPointsAnnotations();
    pointAnnotationsRef.current.map((annotation) => {
      const entityId = createActivity(annotation);
      if (entityId) {
        pointsEntityIdsRef.current = [...pointsEntityIdsRef.current, entityId];
      }
    });
  };

  useEffect(() => {
    if (
      tvWidget &&
      pointAnnotations?.length &&
      !initializedPoints.current &&
      startTimestamp &&
      lowestVisibleTimestamp &&
      startTimestamp >= lowestVisibleTimestamp
    ) {
      drawPointsAnnotations();
      initializedPoints.current = true;
    }
  }, [pointAnnotations, tvWidget, startTimestamp, lowestVisibleTimestamp]);

  useEffect(() => {
    onExtendClick.current = () => {
      setPriceRangeOnChange(tvWidget, initialPriceScale);
    };
  }, [allLineAnnotationsPrices, tvWidget, initialPriceScale]);

  const setOrderLine = (
    order: IOrderLineAdapter,
    annotation: LineAnnotation
  ) => {
    const color = annotation.borderColor;
    let text = annotation.text;
    if (!text) {
      text = annotation.orderType
        ? `(${annotation?.orderNumber}) ${
            TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME[annotation.orderType]
          }`
        : "order";
    }

    order
      .setPrice(annotation.yMax)
      .setLineWidth(annotation.borderWidth)
      .setLineColor(color);

    if (annotation.isDashed) {
      order.setLineStyle(1);
    }
    if (annotation.distance) {
      order
        .setText(text)
        .setQuantity(annotation.distance)
        .setBodyTextColor(color)
        .setBodyBorderColor(color)
        .setBodyBackgroundColor(chartBgColor)
        .setQuantityBorderColor(color)
        .setQuantityBackgroundColor(color)
        .setQuantityTextColor(chartBgColor);
    } else {
      order
        .setText("")
        .setQuantity(text)
        .setQuantityTextColor(color)
        .setQuantityBorderColor(color)
        .setQuantityBackgroundColor(chartBgColor);
    }
  };

  const createOrderLine = (annotation: LineAnnotation) => {
    if (tvWidgetRef.current) {
      if (annotation.isSimpleLine) {
        const entityId = createSimpleLine(annotation);
        if (entityId) {
          entityIdsRef.current = [...entityIdsRef.current, entityId];
        }
      } else {
        const order = tvWidgetRef.current
          .chart()
          ?.createOrderLine({ disableUndo: true });
        setOrderLine(order, annotation);
        ordersRef.current = [...ordersRef.current, order];
      }
    }
  };

  const createShape = (
    price: number,
    time: number | undefined,
    shape: CreateShapeOptions<object>["shape"],
    overrides: object
  ) => {
    return tvWidgetRef.current?.chart().createShape(
      { price, time: time || 0 },
      {
        shape,
        overrides,
        zOrder: "top",
        lock: true,
        disableSelection: true,
        disableSave: true,
        disableUndo: true,
      }
    );
  };

  const createActivity = (annotation: PointAnnotation) =>
    createShape(annotation.yValue, annotation.timestamp, "icon", {
      icon: 0xf111,
      size: 12,
      color: annotation.backgroundColor,
    });

  const createSimpleLine = (annotation: LineAnnotation) =>
    createShape(annotation.yMax, undefined, "horizontal_line", {
      linecolor: annotation.borderColor,
      showPrice: false,
      linewidth: 1,
    });

  const clearOrdersAndShapes = () => {
    ordersRef.current.map((order) => {
      order.remove();
    });
    entityIdsRef.current.map((entityId) =>
      tvWidgetRef.current?.chart().removeEntity(entityId)
    );
    ordersRef.current = [];
    entityIdsRef.current = [];
  };

  const onIntervalOrSymbolChange = (
    symbol: string,
    interval: ResolutionString,
    isSymbolChanged?: boolean
  ) => {
    if (isSymbolChanged) {
      setAutoScale();
      setInitialPriceScale(undefined);
    }
    tvWidgetRef.current?.setSymbol(symbol, interval, () => {
      if (EDITED_GRAPH_RANGE_INTERVALS.includes(interval)) {
        const from = minimumTimestamp ?? getChartStartRange(interval);
        tvWidgetRef.current?.chart().setVisibleRange({ from });
      }
      if (isSymbolChanged) {
        clearOrdersAndShapes();
      }
    });
  };

  const updateOrderLines = (
    lineAnnotations: LineAnnotation[],
    isSymbolChanged?: boolean
  ) => {
    clearOrdersAndShapes();

    try {
      lineAnnotations.map((annotation) => {
        createOrderLine(annotation);
      });

      if (!isSymbolChanged || lineAnnotations.length) {
        setPrevLineAnnotations(lineAnnotations);
      }
    } catch (e) {
      Sentry.captureException(
        `Encountered error when running updateOrderLines in TradingView chart: ${e}`
      );
    }
  };

  const setPriceRangeOnChange = (
    _tvWidget?: IChartingLibraryWidget,
    initialPriceScale?: VisiblePriceRange | null,
    isInitialExtend?: boolean
  ) => {
    if (allLineAnnotationsPrices && initialPriceScale) {
      const lineAnnotationsPrices = isInitialExtend
        ? closestLineAnnotationsPrices
        : allLineAnnotationsPrices;
      const minPrice = Math.min(...lineAnnotationsPrices);
      const maxPrice = Math.max(...lineAnnotationsPrices);
      const newPriceRange = getNewPriceRange(
        minPrice,
        maxPrice,
        initialPriceScale
      );
      setExtendToAnnotationsRange(newPriceRange);
      getSourcePriceScale()?.setVisiblePriceRange(newPriceRange);
    }
  };

  const startTimeFrame = useMemo(() => {
    if (!startTimestamp || !endTimestamp) return;
    const now = Date.now() / 1000;
    const chartEndTimestamp = Math.min(now, endTimestamp ?? Infinity);
    const hour = 3600;
    const minRange = hour * 4;
    const timeRange = Math.max(minRange, chartEndTimestamp - startTimestamp);
    const padding = Math.max(Math.round(timeRange * 0.05), hour / 2);
    const to = Math.ceil(chartEndTimestamp + padding);
    const from = Math.floor(Math.min(startTimestamp - padding, to - minRange));
    return { from: Math.max(from, minimumTimestamp ?? -Infinity), to };
  }, [startTimestamp, endTimestamp]);

  const timeFrames = useMemo(() => {
    let timeFrames: TimeFrameItem[] = [];
    timeRanges.map((timeRange) => {
      if (timeRange === TIME_RANGE_START) {
        timeFrames.push(getStartTimeframe(startTimestamp));
      } else {
        timeFrames.push({
          text: timeRange,
          resolution: TIME_RANGE_TO_RESOLUTION[timeRange] as ResolutionString,
          description: timeRange,
        });
      }
    });
    return timeFrames;
  }, [timeRanges, startTimestamp]);

  const getTimeFrameProperty = (property: keyof TimeFrameItem) => {
    return timeFrames.find(
      (timeFrameItem) => timeFrameItem.description === timeFrame
    )?.[property];
  };

  const getInitialTimeFrame = () => {
    if (timeFrame === TIME_RANGE_START && startTimeFrame) {
      return startTimeFrame;
    }
    return getTimeFrameProperty("text");
  };

  const getInitialInterval = () => {
    if (timeFrame === TIME_RANGE_START && startTimeFrame) {
      return getResolutionByTimeFrame(startTimeFrame) as ResolutionString;
    }
    return (getTimeFrameProperty("resolution") || "5") as ResolutionString;
  };

  let disabledFeatures = [
    "header_symbol_search",
    "symbol_search_hot_key",
    "header_compare",
    "display_market_status",
    "timezone_menu",
  ];
  if (minimize) disabledFeatures = [...disabledFeatures, "left_toolbar"];
  // New look on phone: drawer charts keep only the interval picker, the
  // range/fit buttons and fullscreen in the header; the rest of the chrome
  // (chart type, indicators, undo/redo, settings, screenshot, the OHLC legend,
  // the bottom date-range bar and the zoom pill) doesn't fit a 358px pane.
  // The same compact chrome for the new look's overview card chart (the
  // portfolio "Price Change"): in a short card the toolbars, legend and
  // bottom bar left the candles a thin strip.
  if (theme.isNewLook && minimize && (isBottomNavigator || isCard)) {
    disabledFeatures = [...disabledFeatures, ...NEW_LOOK_PHONE_HIDDEN_FEATURES];
  }

  let commonOverrides = {};
  COMMON_PROPERTIES_CHART_TYPES.map((chartType) => {
    commonOverrides = {
      ...commonOverrides,
      [`mainSeriesProperties.${chartType}Style.upColor`]: chartColors.upColor,
      [`mainSeriesProperties.${chartType}Style.downColor`]:
        chartColors.downColor,
    };
    if (chartType !== "bar" && chartType !== "column") {
      commonOverrides = {
        ...commonOverrides,
        [`mainSeriesProperties.${chartType}Style.borderUpColor`]:
          chartColors.upColor,
        [`mainSeriesProperties.${chartType}Style.borderDownColor`]:
          chartColors.downColor,
        [`mainSeriesProperties.${chartType}Style.wickUpColor`]:
          chartColors.upColor,
        [`mainSeriesProperties.${chartType}Style.wickDownColor`]:
          chartColors.downColor,
      };
    }
  });

  const getChartOverrides = () => {
    return {
      ...commonOverrides,
      "scalesProperties.showStudyPlotLabels": false,
      "scalesProperties.showStudyLastValue": false,
      "scalesProperties.textColor": theme.colors.primaryText,
      "paneProperties.legendProperties.showStudyTitles": false,
      "paneProperties.legendProperties.showSeriesTitle": false,
      "paneProperties.legendProperties.showStudyValues": false,
      "paneProperties.backgroundType": "solid" as ColorTypes,
      "paneProperties.background": chartBgColor,
      "paneProperties.vertGridProperties.color": chartGridColor,
      "paneProperties.horzGridProperties.color": chartGridColor,
      "mainSeriesProperties.statusViewStyle.showInterval": false,
      "mainSeriesProperties.statusViewStyle.showExchange": false,
      "mainSeriesProperties.priceLineColor": theme.colors.tvCurrentPriceColor,
      "mainSeriesProperties.showPriceLine": false,
      "mainSeriesProperties.lineStyle.color": chartColors.graphColor,
      "mainSeriesProperties.areaStyle.color1": hexToRGB(
        chartColors.graphColor,
        0.2
      ),
      "mainSeriesProperties.areaStyle.color2": hexToRGB(
        chartColors.graphColor,
        0.05
      ),
      "mainSeriesProperties.areaStyle.linecolor": chartColors.graphColor,
      "mainSeriesProperties.baselineStyle.topLineColor": chartColors.upColor,
      "mainSeriesProperties.baselineStyle.bottomLineColor":
        chartColors.downColor,
      "mainSeriesProperties.baselineStyle.topFillColor1": hexToRGB(
        chartColors.upColor,
        0.28
      ),
      "mainSeriesProperties.baselineStyle.topFillColor2": hexToRGB(
        chartColors.upColor,
        0.05
      ),
      "mainSeriesProperties.baselineStyle.bottomFillColor1": hexToRGB(
        chartColors.downColor,
        0.05
      ),
      "mainSeriesProperties.baselineStyle.bottomFillColor2": hexToRGB(
        chartColors.downColor,
        0.28
      ),
      "mainSeriesProperties.hiloStyle.color": chartColors.graphColor,
      "mainSeriesProperties.hiloStyle.borderColor": chartColors.graphColor,
      "mainSeriesProperties.hiloStyle.labelColor": chartColors.graphColor,
    };
  };

  const getDefaultProps = () => {
    return {
      locale: "en" as LanguageCode,
      interval: getInitialInterval(),
      timeFrame: getInitialTimeFrame(),
      datafeedUrl: getApiBaseUrl().concat("/tradingview"),
      libraryPath: "/charting_library/",
      custom_css_url: theme.isNewLook
        ? "../css/newLookTvChartTheme.css"
        : isCard
        ? "../css/cardTvChartTheme.css"
        : "../css/tvChartTheme.css",
      fullscreen: false,
      autosize: true,
      disabled_features: disabledFeatures as ChartingLibraryFeatureset[],
      loading_screen: {
        backgroundColor: chartBgColor,
        foregroundColor: theme.colors.primaryBlue,
      },
      studies_overrides: {
        "volume.volume.color.0": chartColors.downColor,
        "volume.volume.color.1": chartColors.upColor,
      },
    };
  };

  useEffect(() => {
    if (!symbol || tvWidget || isLoadingMinimumTimestamp) return;
    const Datafeed = (window as any).Datafeeds?.UDFCompatibleDatafeed;
    if (!Datafeed) {
      if (datafeedRetryCount >= DATAFEED_MAX_RETRY_COUNT) {
        if (!datafeedLoadErrorReported.current) {
          datafeedLoadErrorReported.current = true;
          Sentry.captureException(
            new Error("TradingView datafeed script failed to load")
          );
        }
        return;
      }

      const retryTimeout = setTimeout(() => {
        setDatafeedRetryCount((count) => count + 1);
      }, getDatafeedRetryDelay(datafeedRetryCount));
      return () => {
        clearTimeout(retryTimeout);
      };
    }

    const defaultProps = getDefaultProps();
    const tradingViewWidget = new widget({
      symbol,
      datafeed: new Datafeed(defaultProps.datafeedUrl, undefined, {
        maxResponseLength: 4000,
        expectedOrder: "latestFirst",
      }),
      interval: defaultProps.interval,
      container: chartContainerRef.current!,
      library_path: defaultProps.libraryPath,
      locale: defaultProps.locale,
      fullscreen: defaultProps.fullscreen,
      autosize: defaultProps.autosize,
      disabled_features: defaultProps.disabled_features,
      studies_overrides: defaultProps.studies_overrides,
      theme: isDarkMode ? "Dark" : "Light",
      custom_css_url: defaultProps.custom_css_url,
      loading_screen: defaultProps.loading_screen,
      timeframe: defaultProps.timeFrame,
      time_frames: timeFrames,
    });
    if (theme.isNewLook && window.devicePixelRatio >= 2) {
      pinChartCanvasSizing(chartContainerRef.current);
    }

    tradingViewWidget.onChartReady(() => {
      const chart = tradingViewWidget?.chart();
      tradingViewWidget?.applyOverrides(getChartOverrides());

      if (startTimeFrame) {
        const button = tradingViewWidget.createButton({
          align: isBottomNavigator ? "left" : "right",
          useTradingViewStyle: false,
        });
        button.textContent = `Show ${object || "Full"} Time Range`;
        button.style.padding = "4px 8px 6px";
        button.style.margin = "0 4px";
        button.addEventListener("mouseenter", () => {
          button.style.color = theme.colors.primaryBlue;
        });
        button.addEventListener("mouseleave", () => {
          if (button.style.background === "transparent") {
            button.style.color = theme.colors.primaryGrey;
          }
        });
        const resolution = getResolutionByTimeFrame(
          startTimeFrame
        ) as ResolutionString;
        button.addEventListener("click", () => {
          chart?.setResolution(resolution, () => {
            chart?.setVisibleRange(startTimeFrame);
          });
        });
        chart?.onVisibleRangeChanged().subscribe(null, (range) => {
          const resolutionInSeconds = RESOLUTION_TO_SECONDS[resolution];
          const barCount = 4;
          const isFromSimilar =
            Math.abs(startTimeFrame.from - range.from) <=
            resolutionInSeconds * barCount;
          const isToSimilar =
            Math.abs(startTimeFrame.to - range.to) <=
            resolutionInSeconds * barCount;
          const isRangeSimilar = isFromSimilar && isToSimilar;

          if (isRangeSimilar) {
            button.style.color = theme.colors.primaryBlue;
            button.style.background = theme.colors.lightBlue;
            button.style.borderRadius = "6px";
          } else {
            button.style.color = theme.colors.primaryGrey;
            button.style.background = "transparent";
          }
        });
      }

      if (showExtendButton) {
        tradingViewWidget?.headerReady().then(() => {
          let button = tradingViewWidget.createButton({
            align: "right",
            useTradingViewStyle: false,
          });
          button.setAttribute("title", "Fit to Orders");
          button.addEventListener("click", () => {
            onExtendClick.current();
          });
          button.innerHTML = ExtendIcon;
        });
      }

      chart.onIntervalChanged().subscribe(null, (interval) => {
        setSymbolToLoad(undefined);
        onIntervalOrSymbolChange(
          tradingViewWidget?.symbolInterval().symbol,
          interval
        );
        setInitialPriceScale(undefined);
        setAutoScale();
      });

      chart?.onSymbolChanged().subscribe(null, () => {
        setSymbolToLoad(undefined);
        setSymbolChanged(true);
      });

      chart?.onVisibleRangeChanged().subscribe(null, (range) => {
        if (
          !initializedPoints.current &&
          (!lowestVisibleTimestamp || range.from < lowestVisibleTimestamp)
        ) {
          setLowestVisibleTimestamp(range.from);
        }
      });

      chart.onDataLoaded().subscribe(null, () => {
        setSymbolToLoad(tradingViewWidget?.symbolInterval().symbol);
        drawPointsAnnotations();
        setRequestInProgress((num) => num + 1);
      });

      tvWidgetRef.current = tradingViewWidget;
      setTvWidget(tradingViewWidget);
    });
  }, [symbol, isLoadingMinimumTimestamp, startTimeFrame, datafeedRetryCount]);

  useEffect(() => {
    return () => {
      tvWidgetRef.current?.remove();
    };
  }, []);

  return {
    isLoading,
    chartContainerRef,
    isValidInExchange,
  };
};

export default useTradingViewChart;

const DATAFEED_INITIAL_RETRY_DELAY_MS = 50;
const DATAFEED_MAX_RETRY_DELAY_MS = 1000;
const DATAFEED_MAX_RETRY_COUNT = 65;
const EDITED_GRAPH_RANGE_INTERVALS = ["1W", "1M"];

const NEW_LOOK_PHONE_HIDDEN_FEATURES = [
  "header_chart_type",
  "header_indicators",
  "header_undo_redo",
  "header_settings",
  "header_screenshot",
  "legend_widget",
  "timeframes_toolbar",
  "control_bar",
];

const COMMON_PROPERTIES_CHART_TYPES = [
  "bar",
  "candle",
  "hollowCandle",
  "column",
  "ha",
];

const TIME_RANGE_TO_RESOLUTION = {
  [TIME_RANGE_1_HOUR]: "1",
  [TIME_RANGE_1_DAY]: "5",
  [TIME_RANGE_1_WEEK]: "15",
  [TIME_RANGE_1_MONTHS]: "60",
  [TIME_RANGE_3_MONTHS]: "240",
  [TIME_RANGE_1_YEAR]: "1d",
};

const RESOLUTION_TO_SECONDS = {
  "1": SECONDS_IN_MINUTE,
  "5": 5 * SECONDS_IN_MINUTE,
  "15": 15 * SECONDS_IN_MINUTE,
  "60": 60 * SECONDS_IN_MINUTE,
  "240": 240 * SECONDS_IN_MINUTE,
  "1d": SECONDS_IN_DAY,
};

const ExtendIcon =
  '<svg class="icon" viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor"><path d="M855 160.1l-189.2 23.5c-6.6.8-9.3 8.8-4.7 13.5l54.7 54.7-153.5 153.5a8.03 8.03 0 000 11.3l45.1 45.1c3.1 3.1 8.2 3.1 11.3 0l153.6-153.6 54.7 54.7a7.94 7.94 0 0013.5-4.7L863.9 169a7.9 7.9 0 00-8.9-8.9zM416.6 562.3a8.03 8.03 0 00-11.3 0L251.8 715.9l-54.7-54.7a7.94 7.94 0 00-13.5 4.7L160.1 855c-.6 5.2 3.7 9.5 8.9 8.9l189.2-23.5c6.6-.8 9.3-8.8 4.7-13.5l-54.7-54.7 153.6-153.6c3.1-3.1 3.1-8.2 0-11.3l-45.2-45z"></path></svg>';

const getDatafeedRetryDelay = (retryCount: number) =>
  Math.min(
    DATAFEED_MAX_RETRY_DELAY_MS,
    DATAFEED_INITIAL_RETRY_DELAY_MS * 2 ** retryCount
  );
